<script lang="ts" setup>
import { computed, type PropType } from 'vue'
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue'

interface PumpInfoRow {
  dataHeader: string
  pumpOne: string
  pumpTwo: string
  pumpThree: string
  pumpFour: string
  totalPumpTime: string
}

interface ConcurrentPumpOverview {
  onePumpMs: number
  twoPumpMs: number
  threePumpMs: number
  fourPumpMs: number
  onePumpTime: string
  twoPumpTime: string
  threePumpTime: string
  fourPumpTime: string
}

const props = defineProps({
  pumpInformationData: {
    type: Array as PropType<PumpInfoRow[]>,
    required: true,
    default: () => []
  },
  concurrentPumpOverview: {
    type: Object as PropType<ConcurrentPumpOverview>,
    required: false,
    default: null
  }
});

const { hasIntermediateAccess } = useAuth();

const formatDuration = (totalDays: number) => {
  if (!totalDays || isNaN(totalDays)) return "00:00:00";
  let totalSeconds = Math.round(totalDays * 86400);
  const hours = Math.floor(totalSeconds / 3600);
  totalSeconds %= 3600;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Formats the cell value. 
 * Converts fractional days into HH:mm:ss
 */
const formatValue = (header: string, value: string) => {
  if (value === "0" || value === "" || !value) return "—";

  // Check if this row represents duration (Net, Total, Idle)
  const isDuration = ["Total Time", "Net Time", "Idle Time"].includes(header);
  
  if (isDuration) {
    const totalDays = parseFloat(value);
    if (isNaN(totalDays)) return value;

    return formatDuration(totalDays);
  }

  return value; // Return Start/Stop dates as they are
};

const pumpStats = computed(() => {
  const data = props.pumpInformationData || [];
  const netRow = data.find(r => r.dataHeader === 'Net Time');
  const idleRow = data.find(r => r.dataHeader === 'Idle Time');

  const parse = (val: string | undefined) => {
    if (!val) return 0;
    const f = parseFloat(val);
    return isNaN(f) ? 0 : f;
  };

  const getStats = (key: keyof PumpInfoRow) => {
    const net = netRow ? parse(netRow[key] as string) : 0;
    const idle = idleRow ? parse(idleRow[key] as string) : 0;
    const total = net + idle;
    return {
      net,
      idle,
      netPct: total > 0 ? (net / total) * 100 : 0,
      idlePct: total > 0 ? (idle / total) * 100 : 0
    };
  };

  const pumps = [
    { label: 'Pump 1', ...getStats('pumpOne') },
    { label: 'Pump 2', ...getStats('pumpTwo') },
    { label: 'Pump 3', ...getStats('pumpThree') },
    { label: 'Pump 4', ...getStats('pumpFour') },
  ];

  const system = { label: 'System Total', ...getStats('totalPumpTime') };

  return { pumps, system };
});

const concurrentStats = computed(() => {
  const c = props.concurrentPumpOverview;
  if (!c) return null;

  const total = c.onePumpMs + c.twoPumpMs + c.threePumpMs + c.fourPumpMs;
  const twoPlusMs = c.twoPumpMs + c.threePumpMs + c.fourPumpMs;
  const threePlusMs = c.threePumpMs + c.fourPumpMs;
  const pct = (ms: number) => total > 0 ? (ms / total) * 100 : 0;

  const msToHHmmss = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  return {
    onePct: pct(c.onePumpMs),
    twoPct: pct(c.twoPumpMs),
    threePct: pct(c.threePumpMs),
    fourPct: pct(c.fourPumpMs),
    billableTime: msToHHmmss(twoPlusMs),
    threePlusTime: msToHHmmss(threePlusMs),
  };
});

const headers = [
  { title: 'Metric', key: 'dataHeader', sortable: false },
  { title: 'Pump 1', key: 'pumpOne', align: 'end' as const, sortable: false },
  { title: 'Pump 2', key: 'pumpTwo', align: 'end' as const, sortable: false },
  { title: 'Pump 3', key: 'pumpThree', align: 'end' as const, sortable: false },
  { title: 'Pump 4', key: 'pumpFour', align: 'end' as const, sortable: false },
  { title: 'System Total', key: 'totalPumpTime', align: 'end' as const, sortable: false },
];

const rowProps = (data: { item: PumpInfoRow }) => {
  if (data.item.dataHeader === 'Net Time') return { class: 'bg-green-lighten-5' };
  if (data.item.dataHeader === 'Idle Time') return { class: 'bg-amber-lighten-5' };
  return {};
};
</script>


<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-container fluid v-else>
    <v-alert
      v-if="!pumpInformationData || pumpInformationData.length === 0"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      No pump information available.
    </v-alert>

    <template v-else>
      <!-- Placeholder Cards for Charts -->
      <v-row>
        <v-col v-for="(pump, i) in pumpStats.pumps" :key="i" cols="12" sm="6" md="4" lg="2">
          <v-card class="h-100" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
              {{ pump.label }}
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="d-flex rounded overflow-hidden mb-2" style="height: 12px; background-color: #f1f5f9;">
                 <div class="bg-success transition-swing" :style="{ width: pump.netPct + '%' }" :title="`Net: ${formatDuration(pump.net)}`"></div>
                 <div class="bg-warning transition-swing" :style="{ width: pump.idlePct + '%' }" :title="`Idle: ${formatDuration(pump.idle)}`"></div>
              </div>
              <div class="d-flex justify-space-between text-caption text-medium-emphasis">
                 <div class="d-flex align-center"><v-badge dot color="success" inline class="mr-1"></v-badge>Net</div>
                 <div class="d-flex align-center"><v-badge dot color="warning" inline class="mr-1"></v-badge>Idle</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <v-col cols="12" sm="6" md="4" lg="4">
          <v-card class="h-100" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
              {{ pumpStats.system.label }}
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="d-flex rounded overflow-hidden mb-2" style="height: 12px; background-color: #f1f5f9;">
                 <div class="bg-success transition-swing" :style="{ width: pumpStats.system.netPct + '%' }" :title="`Net: ${formatDuration(pumpStats.system.net)}`"></div>
                 <div class="bg-warning transition-swing" :style="{ width: pumpStats.system.idlePct + '%' }" :title="`Idle: ${formatDuration(pumpStats.system.idle)}`"></div>
              </div>
              <div class="d-flex justify-space-between text-caption text-medium-emphasis">
                 <div class="d-flex align-center"><v-badge dot color="success" inline class="mr-1"></v-badge>Net</div>
                 <div class="d-flex align-center"><v-badge dot color="warning" inline class="mr-1"></v-badge>Idle</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="mt-6" elevation="2">
        <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
          Pump Details
        </v-card-title>
        <v-divider></v-divider>
        <v-data-table
          :headers="headers"
          :items="pumpInformationData"
          density="compact"
          class="text-body-2"
          hover
          :row-props="rowProps"
        >
          <template v-slot:item.dataHeader="{ item }">
            <span class="font-weight-bold text-blue-grey-darken-3">{{ item.dataHeader }}</span>
          </template>
          <template v-slot:item.pumpOne="{ item }"><span class="font-monospace">{{ formatValue(item.dataHeader, item.pumpOne) }}</span></template>
          <template v-slot:item.pumpTwo="{ item }"><span class="font-monospace">{{ formatValue(item.dataHeader, item.pumpTwo) }}</span></template>
          <template v-slot:item.pumpThree="{ item }"><span class="font-monospace">{{ formatValue(item.dataHeader, item.pumpThree) }}</span></template>
          <template v-slot:item.pumpFour="{ item }"><span class="font-monospace">{{ formatValue(item.dataHeader, item.pumpFour) }}</span></template>
          <template v-slot:item.totalPumpTime="{ item }">
            <span class="font-weight-bold text-blue-grey-darken-3 font-monospace">{{ formatValue(item.dataHeader, item.totalPumpTime) }}</span>
          </template>
        </v-data-table>
        <v-card-text class="text-caption text-medium-emphasis bg-grey-lighten-5 border-t">
          <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
          Time values (Total/Net/Idle) are displayed in fractional days.
        </v-card-text>
      </v-card>

      <!-- Concurrent Pump Overview -->
      <v-card v-if="concurrentPumpOverview && concurrentStats" class="mt-6" elevation="2">
        <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4 d-flex align-center flex-wrap gap-2">
          Concurrent Pump Operation
          <v-chip class="ml-2" color="blue-grey" size="small" variant="tonal">
            2+ pumps: {{ concurrentStats.billableTime }}
          </v-chip>
          <v-chip class="ml-1" color="green-darken-2" size="small" variant="tonal">
            3+ pumps: {{ concurrentStats.threePlusTime }}
          </v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <div class="d-flex rounded overflow-hidden mb-4" style="height: 16px; width: 100%; background-color: #f1f5f9;">
            <div :style="{ width: concurrentStats.onePct + '%', backgroundColor: '#BDBDBD', transition: 'width 0.5s' }" :title="`1 pump: ${concurrentPumpOverview.onePumpTime}`"></div>
            <div :style="{ width: concurrentStats.twoPct + '%', backgroundColor: '#A5D6A7', transition: 'width 0.5s' }" :title="`2 pumps: ${concurrentPumpOverview.twoPumpTime}`"></div>
            <div :style="{ width: concurrentStats.threePct + '%', backgroundColor: '#4CAF50', transition: 'width 0.5s' }" :title="`3 pumps: ${concurrentPumpOverview.threePumpTime}`"></div>
            <div :style="{ width: concurrentStats.fourPct + '%', backgroundColor: '#2E7D32', transition: 'width 0.5s' }" :title="`4 pumps: ${concurrentPumpOverview.fourPumpTime}`"></div>
          </div>
          <v-row dense>
            <v-col cols="12" sm="6" md="3">
              <div class="d-flex align-center text-caption text-medium-emphasis mb-1">
                <span class="concurrent-dot mr-1" style="background-color: #BDBDBD;"></span>
                1 pump
              </div>
              <div class="font-monospace text-body-2 pl-4">{{ concurrentPumpOverview.onePumpTime }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="d-flex align-center text-caption text-medium-emphasis mb-1">
                <span class="concurrent-dot mr-1" style="background-color: #A5D6A7;"></span>
                2 pumps simultaneously
              </div>
              <div class="font-monospace text-body-2 pl-4">{{ concurrentPumpOverview.twoPumpTime }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="d-flex align-center text-caption text-medium-emphasis mb-1">
                <span class="concurrent-dot mr-1" style="background-color: #4CAF50;"></span>
                3 pumps simultaneously
              </div>
              <div class="font-monospace text-body-2 pl-4">{{ concurrentPumpOverview.threePumpTime }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="d-flex align-center text-caption text-medium-emphasis mb-1">
                <span class="concurrent-dot mr-1" style="background-color: #2E7D32;"></span>
                4 pumps simultaneously
              </div>
              <div class="font-monospace text-body-2 pl-4">{{ concurrentPumpOverview.fourPumpTime }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>


<style scoped>
/* Utility class for monospace font */
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
.transition-swing {
  transition: width 0.5s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.concurrent-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>